import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { CheckException } from '@treasury/domain/arp';
import { CheckExceptionService } from '@treasury/domain/arp/check-exception.service';
import { CompanyOptionModel } from '@treasury/domain/arp/company-option-model.entity';
import { ExceptionCorrection } from '@treasury/domain/arp/exception-correction.entity';
import { InstitutionCheckExceptionReturnReason } from '@treasury/domain/arp/institution-check-exception-return-reason.entity';
import { CheckExceptionsService } from '@treasury/domain/channel/services/positive-pay/check-exceptions-service';
import {
    CheckExceptionReturnReasons,
    ReturnReasonsDescriptions,
} from '@treasury/domain/channel/types';
import {
    CORRECTION_REQUEST_OPTION,
    RETURN_REASON_OPTION,
} from '@treasury/domain/channel/types/arp/constants.js';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmApiError } from '@treasury/domain/shared';
import { LabeledList, TmContainer } from '@treasury/presentation';
import { arrowDownToBracket } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/layout/tm-content';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-badge';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';

import { DeepReactive } from '@jack-henry/frontend-utils/lit';
// eslint-disable-next-line no-duplicate-imports
import { TmFooter } from '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-labeled-list';
import '@treasury/presentation/components/tm-loader';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import { type CheckImage } from '../../types/check-exception.types';
import '../partials/components/header-text.component';
import '../partials/components/select-from-list.component';
import '../partials/detail/check-exception-comment-editor';
import '../partials/detail/check-exception-correction';
import '../partials/detail/correction-request-confirmation';
import { CheckExceptionVm } from '../view-models/checkException';

export const tagName = 'check-exception-detail-container';
@customElement(tagName)
export class CheckExceptionDetailContainer extends TmContainer {
    @DeepReactive()
    public checkException?: CheckException;

    @property()
    public backToTableView?: () => void;

    @property()
    public returnReasons: InstitutionCheckExceptionReturnReason[] = [];

    protected readonly verifiedPropNames: (keyof this)[] = [
        'checkException',
        'backToTableView',
        'returnReasons',
    ];

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private checkExceptionService: CheckExceptionService;

    @state()
    private checkImages?: CheckImage;

    @state()
    private selectedReturnReason?: InstitutionCheckExceptionReturnReason;

    @state()
    private requestingCorrection = false;

    @state()
    private reviewingCorrectionRequest = false;

    @state()
    private viewingAudit = false;

    @state()
    private showCorrectionRequestConfirmation = false;

    @state()
    private correctionRequestResponse = false;

    @state()
    private footerConfig: ButtonConfig[] = [
        {
            error: true,
            text: 'Return',
            onClick: () => {
                if (!this.checkException) {
                    throw new Error('Cannot pay. No check exception.');
                }

                this.returnException(this.checkException);
            },
            size: 'large',
        },
        {
            success: true,
            text: 'Pay',
            onClick: () => {
                if (!this.checkException) {
                    throw new Error('Cannot pay. No check exception.');
                }

                this.payException(this.checkException);
            },
            size: 'large',
        },
    ];

    @state()
    private correction?: ExceptionCorrection;

    @state()
    private correctionIsValid = false;

    @state()
    private attachment?: File;

    @state()
    private attachmentHasUpdated = false;

    @state()
    private hasReturnReasons = false;

    @state()
    private hasRequestCorrection = false;

    @state()
    private fileName?: string;

    @state()
    private options: CompanyOptionModel[] = [];

    private viewModel = new CheckExceptionVm();

    private get showFooter() {
        const showingSubView =
            this.requestingCorrection ||
            this.showCorrectionRequestConfirmation ||
            this.reviewingCorrectionRequest;
        return !this.checkExceptionService.isAfterCutoff || showingSubView;
    }

    public async firstUpdated() {
        this.options = await this.checkExceptionService.getPositivePayCompanyConfiguration();
        const returnReasonFF = await (
            await this.featureFlagService
        ).isEnabled(Feature.PositivePayReturnReason);
        this.hasReturnReasons = returnReasonFF && this.hasOption(RETURN_REASON_OPTION);
        this.hasRequestCorrection = returnReasonFF && this.hasOption(CORRECTION_REQUEST_OPTION);

        if (this.checkException) {
            this.initHeaderAndFooter(this.checkException);
            this.viewModel = new CheckExceptionVm(this.checkException);
            this.selectedReturnReason = this.returnReasons.find(reason => {
                const exceptionReason = this.checkException
                    ? this.checkException.returnReasonDescription?.replace(/\s/g, '')
                    : '';
                const reasonFromList = reason.description?.replace(/\s/g, '');
                return exceptionReason === reasonFromList;
            });

            if (this.checkException.protected) {
                this.footerConfig = [];
            }
        }

        this.tryFetch(this.getCheckImages.bind(this), imageData => {
            this.checkImages = imageData;
        });
        this.tryGetAttachment();
    }

    public render() {
        if (!this.checkException) {
            return nothing;
        }

        const { checkException } = this;
        const vm = new CheckExceptionVm(checkException);
        const returnAction = () => this.navToRequestCorrection(checkException);
        const changeReturnReason = (reason: InstitutionCheckExceptionReturnReason) =>
            this.setReturnReason(reason, checkException);

        let contentBody = html`
            <tm-section class="pl-6 py-4">
                <div class="detail-header border-b border-dashed">
                    <div class="flex items-end justify-between  pb-2.5 pr-4">
                        <div class="flex items-baseline">
                            <span class="text-lg ">Check</span>
                            <span class="check-number text-xs weight p-1 text-secondary">
                                ${this.checkException.checkNumber}
                            </span>
                        </div>
                        <tm-badge>${this.checkException.exceptionReason}</tm-badge>
                    </div>
                    <div class="text-right pr-4"></div>
                    <div class="flex pb-4 pr-4">
                        <div class="min-w-24">
                            <div class="text-sm text-secondary">Paid</div>
                            <div class="text-xl amount">${this.viewModel?.paidAmount}</div>
                        </div>
                        <div class="min-w-24 ">
                            <div class="text-sm text-secondary">Issued</div>
                            <div class="text-xl amount">${this.viewModel?.issuedAmount}</div>
                        </div>
                    </div>
                </div>
                <div class="py-4 text-left pr-4 bt-2 border-b border-dashed">
                    <tm-labeled-list
                        .list=${new LabeledList(
                            this.viewModel,
                            [
                                'accountNumber',
                                'postedDate',
                                'issuedDate',
                                'issuedPayee',
                                'protected',
                            ],
                            { accountNumber: 'Account' }
                        )}
                    >
                    </tm-labeled-list>
                    ${this.viewingAudit
                        ? html`<div class="py-2 audit-log">
                              <div class="pb-2 font-medium">Audit</div>
                              ${this.checkException.audits?.map(
                                  audit => html`
                                      <div class="p-1 audit-time">
                                          ${vm.formatDateTime(audit.timestamp)} : ${audit.username}
                                          : ${audit.action}
                                      </div>
                                  `
                              )}
                          </div>`
                        : nothing}
                    <tm-button
                        class="pt-3 audit-button"
                        @click=${() => {
                            this.viewingAudit = !this.viewingAudit;
                        }}
                        >${this.viewingAudit ? 'Hide Audit' : 'View Audit'}</tm-button
                    >
                </div>
                ${this.hasReturnReasons
                    ? html`<div
                          class="return-reason flex justify-between items-center h-14 pr-4 border-b"
                      >
                          <span class="return-reason-label">Return Reason</span>
                          <tm-slider
                              .header=${html`<header-text
                                  .header=${'Return Reason'}
                                  .subheader=${this.checkException.checkNumber
                                      ? `Check ${this.checkException.checkNumber}`
                                      : ''}
                              ></header-text>`}
                              .value=${this.checkException.returnReasonDescription ===
                              ReturnReasonsDescriptions.None
                                  ? this.renderNoneTextForSliderPreview()
                                  : this.checkException.returnReasonDescription}
                          >
                              <select-from-list
                                  .list=${[
                                      { description: 'None' },
                                      ...this.checkExceptionService.returnReasons,
                                  ]}
                                  .changeItem=${changeReturnReason}
                                  .selectedItem=${this.selectedReturnReason}
                                  .getDisplayNameFromListItem=${(
                                      item: CheckExceptionReturnReasons
                                  ) => item.description}
                                  .getIdFromListItem=${(item: CheckExceptionReturnReasons) =>
                                      item.returnReasonUniqueId}
                              ></select-from-list>
                          </tm-slider>
                      </div>`
                    : nothing}

                <div class="comment flex justify-between items-center h-14 pr-4 border-b">
                    <span>Comment</span>
                    <tm-slider
                        header="Add Comment"
                        .value=${this.checkException.editComment || undefined}
                        .placeholder=${this.renderNoneTextForSliderPreview()}
                    >
                        <tm-content>
                            ${this.renderLoader()}
                            <tm-section>
                                <check-exception-comment-editor
                                    .commentText=${this.checkException.editComment}
                                    .fileName=${this.fileName}
                                    @value-changed=${(e: CustomEvent<string>) => {
                                        if (!this.checkException) {
                                            return;
                                        }
                                        this.checkException.editComment = e.detail;
                                    }}
                                    @attachment-changed=${(e: CustomEvent<File>) => {
                                        this.fileName = e.detail.name;
                                        this.attachment = e.detail;
                                    }}
                                ></check-exception-comment-editor>
                            </tm-section>
                        </tm-content>
                        <tm-footer
                            id="comment-done"
                            .buttonConfig=${[
                                {
                                    importance: 'primary',
                                    text: 'Save',
                                    onClick: (e: Event) => {
                                        if (!this.checkException) {
                                            throw new Error('Could not save check exception.');
                                        }
                                        this.saveCommentAndAttachment(e, this.checkException);
                                    },
                                    size: 'large',
                                },
                            ]}
                            .visible=${this.showFooter}
                        ></tm-footer>
                    </tm-slider>
                </div>

                <div class="check-images pr-4 py-2">
                    <span>Check Images</span>
                    ${this.renderCheckImages()}
                </div>
                ${this.hasRequestCorrection
                    ? html`<div class="request-correction pr-4 pt-3 w-full">
                          <tm-button
                              @click=${() => this.navToRequestCorrection(checkException)}
                              class="w-full"
                              >Correction Request</tm-button
                          >
                      </div>`
                    : nothing}
            </tm-section>
        `;

        if (this.requestingCorrection) {
            contentBody = html` ${this.renderLoader()}
                <check-exception-correction
                    .checkException=${this.checkException}
                    .reviewingCorrectionRequest=${this.reviewingCorrectionRequest}
                    .returnToCorrectionOverviewAction=${returnAction}
                    .nextStep=${() => this.reviewCorrectionRequest(checkException)}
                    .correctionPayloadSetter=${(c: ExceptionCorrection) => (this.correction = c)}
                    @correction-changed=${(e: CustomEvent) => {
                        this.correctionIsValid = e.detail.isValid;
                        this.configureFooter([
                            {
                                text: 'Review',
                                size: 'large',
                                disabled: !this.correctionIsValid,
                                onClick: () => this.reviewCorrectionRequest(checkException),
                            },
                        ]);
                    }}
                ></check-exception-correction>`;
        }
        if (this.showCorrectionRequestConfirmation) {
            contentBody = html`<correction-request-confirmation
                .requestSuccessful=${this.correctionRequestResponse}
            ></correction-request-confirmation>`;
        }

        const footer =
            Array.isArray(this.footerConfig) && this.footerConfig.length === 0
                ? nothing
                : html`<tm-footer
                      .buttonConfig=${this.footerConfig as ButtonConfig[]}
                      .direction=${'row'}
                      .visible=${this.showFooter}
                      id="detail-footer"
                      class="${classMap({
                          absolute: this.requestingCorrection,
                      })}"
                  >
                  </tm-footer>`;

        return html`
            <tm-content
                class="${classMap({
                    'content-height': !this.checkExceptionService.isAfterCutoff,
                    'after-cutoff-content-height': !!this.checkExceptionService.isAfterCutoff,
                })}"
            >
                ${contentBody}
            </tm-content>
            ${footer}
        `;
    }

    protected async updated(): Promise<void> {
        if (this.checkException) {
            if (this.attachmentHasUpdated && this.checkException.hasAttachment) {
                await this.tryGetAttachment();
            }
            if (this.checkException.returnReasonId) {
                const newFooterConfig = this.footerConfig.map((buttonConfig, index) => {
                    if (index === 1 && !buttonConfig.disabled) {
                        return { ...buttonConfig, disabled: true };
                    }
                    return buttonConfig;
                });

                if (JSON.stringify(newFooterConfig) !== JSON.stringify(this.footerConfig)) {
                    this.footerConfig = newFooterConfig;
                }
            }
        }
    }

    private hasOption(optionName: string) {
        const foundOption = this.options.find(option => option.name === optionName);
        return (foundOption && foundOption.value === '1') || false;
    }

    private back() {
        this.checkException?.revertCommentEdit();
        if (this.backToTableView) {
            this.backToTableView();
        }
    }

    private async tryGetAttachment() {
        if (!this.checkException) {
            throw new Error('Cannot get attachment. No check exception available.');
        }

        const { hasAttachment, guid } = this.checkException;
        if (hasAttachment) {
            await this.tryFetch(
                () => this.checkExceptionService.getAttachmentName(guid),
                res => {
                    if (res) {
                        this.fileName = res;
                    } else {
                        throw new Error('unable to get attachment name');
                    }
                }
            );
            this.attachmentHasUpdated = false;
        }
    }

    private async getCheckImages() {
        if (!this.checkException) {
            throw new Error('Cannot get check images. No check exception available.');
        }

        return this.checkException.checkImageNumber
            ? CheckExceptionsService.fetchCheckImages(this.checkException.checkImageNumber ?? '')
            : {
                  error: true,
                  backImage: '',
                  frontImage: '',
                  checkId: '',
              };
    }

    private returnException(exception: CheckException) {
        exception.return();
        exception.decisionModified = true;
        this.back();
    }

    private payException(exception: CheckException) {
        exception.pay();
        exception.decisionModified = true;
        this.back();
    }

    private initHeaderAndFooter(exception: CheckException) {
        this.headerService.configure({
            title: 'Check Exception Detail',
            backAction: () => this.back(),
        });
        const footerConfig: Array<ButtonConfig> = [
            {
                error: true,
                text: 'Return',
                onClick: () => this.returnException(exception),
                classes: 'px-1 w-1/2',
                size: 'large',
            },
            {
                success: true,
                text: 'Pay',
                onClick: () => this.payException(exception),
                classes: 'px-1 w-1/2',
                size: 'large',
                disabled: exception.returnReasonDescription !== ReturnReasonsDescriptions.None,
            },
        ];
        this.configureFooter(footerConfig);
    }

    private configureFooter(details: Array<ButtonConfig>) {
        this.footerConfig = details;
    }

    private returnToDetailView(exception: CheckException) {
        this.showCorrectionRequestConfirmation = false;
        this.requestingCorrection = false;
        this.viewingAudit = false;
        this.correctionIsValid = false;
        this.initHeaderAndFooter(exception);
    }

    private async submitCorrectionRequest(exception: CheckException) {
        await this.configureFooter([
            {
                ...this.footerConfig[0],
                disabled: true,
            },
        ]);
        if (!this.correction) {
            throw new Error('correction data not found');
        }

        return this.tryFetch(
            async () =>
                this.correction
                    ? this.checkExceptionService.submitCheckExceptionCorrection(this.correction)
                    : false,
            result => {
                this.correctionRequestResponse = result;
                this.headerService.configure({
                    title: this.renderCheckCorrectionHeader(exception),
                    backAction: () => this.returnToDetailView(exception),
                });
                this.configureFooter([
                    {
                        importance: 'primary',
                        size: 'large',
                        text: 'Return to Check Exceptions',
                        onClick: () => this.back(),
                    },
                ]);
                this.showCorrectionRequestConfirmation = true;
            }
        );
    }

    private reviewCorrectionRequest(exception: CheckException) {
        this.reviewingCorrectionRequest = true;
        this.headerService.configure({
            title: this.renderCheckCorrectionHeader(exception),
            backAction: () => this.navToRequestCorrection(exception),
        });
        this.configureFooter([
            {
                importance: 'primary',
                size: 'large',
                text: 'Send Request',
                onClick: () => this.submitCorrectionRequest(exception),
            },
        ]);
    }

    private navToRequestCorrection(exception: CheckException) {
        this.requestingCorrection = true;
        this.reviewingCorrectionRequest = false;
        this.headerService.configure({
            title: this.renderCheckCorrectionHeader(exception),
            menuItems: [],
            backAction: () => this.returnToDetailView(exception),
        });
        this.configureFooter([
            {
                text: 'Review',
                size: 'large',
                disabled: !this.correctionIsValid,
                onClick: () => this.reviewCorrectionRequest(exception),
            },
        ]);
    }

    private updatePayButtonDisabledState(isDisabled: boolean) {
        this.footerConfig = this.footerConfig.map(buttonConfig => {
            if (buttonConfig.text === 'Pay') {
                return { ...buttonConfig, disabled: isDisabled };
            }
            return buttonConfig;
        });
    }

    private setReturnReason(
        reason: InstitutionCheckExceptionReturnReason,
        exception: CheckException
    ) {
        if (reason.description === ReturnReasonsDescriptions.None) {
            this.selectedReturnReason = undefined;
            exception.returnReasonDescription = ReturnReasonsDescriptions.None;
            this.updatePayButtonDisabledState(false);
        } else {
            this.selectedReturnReason = reason;
            exception.returnReasonDescription = reason.description;
            this.updatePayButtonDisabledState(true);
        }
    }

    private attachmentError(message?: string) {
        if (message) {
            this.notificationService.renderError(message);
        }
        const commentText = html`${this.checkException?.editComment}`;
        const attachmentText = this.attachment
            ? html` and attachment with name <i>${this.attachment?.name}</i>.`
            : nothing;

        this.notificationService.renderError(
            html`<p>An error occurred while attempting to save comment and attachment.</p>
                <p>Unable to save comment: "${commentText}"${attachmentText}</p>`
        );
    }

    private async saveCommentAndAttachment(e: Event, exception: CheckException) {
        const element = e.currentTarget;
        await this.tryFetch(
            async () =>
                this.checkExceptionService.saveAttachmentAndComment(
                    exception,
                    exception.editComment ?? '',
                    this.attachment
                ),
            (response: boolean) => {
                if (!response) {
                    this.attachmentError();
                    return;
                }
                exception.updateComment();
                if (this.attachment) {
                    this.attachmentHasUpdated = true;
                }
                exception.hasAttachment = response;
            },
            res => this.attachmentError((res as TmApiError)?.message)
        );
        (element as TmFooter).dispatchEvent(
            new Event('close', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private renderCheckCorrectionHeader(exception: CheckException) {
        const { checkNumber } = exception;
        return html`<header-text
            .header=${'Correction Request'}
            .subheader=${checkNumber ? `Check ${checkNumber}` : ''}
        ></header-text>`;
    }

    private renderCheckImages() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }

        const checkImagesHtml = (frontUrl: string, backUrl: string) =>
            html`<div class="check-image border-solid image-front" slot="extra">
                    <img
                        src="data:image/jpeg;base64,${frontUrl}"
                        alt="front of check"
                        class="w-full"
                    />
                </div>
                <br />
                <div class="check-image image-back" slot="extra">
                    <img
                        src="data:image/jpeg;base64,${backUrl}"
                        alt="back of check"
                        class="w-full"
                    />
                </div>
                <div class="flex flex-row-reverse justify-start w-full ">
                    <tm-icon
                        class="hidden"
                        @click=${() =>
                            this.notificationService.renderError(
                                'Cannot download check image files at this time.'
                            )}
                        class="text-right"
                        .height=${'25px'}
                        .width=${'25px'}
                        .icon=${arrowDownToBracket}
                    ></tm-icon>
                </div> `;

        if (!this.checkImages || this.checkImages.error) {
            return html`<div class="flex pb-4 pr-4">
                <div class="min-w-24">
                    <div class="text-sm text-secondary">No check images found</div>
                </div>
            </div>`;
        }
        return checkImagesHtml(
            encodeURI(this.checkImages.frontImage),
            encodeURI(this.checkImages.backImage)
        );
    }

    private renderNoneTextForSliderPreview() {
        return html`<span class="text-gray-300 relative -top-px">none</span>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --link-button-text: var(--jh-color-gray-550);
                }
                :host([theme='dark']) {
                    --link-button-text: var(--jh-color-gray-100);
                }
                .content-height {
                    min-height: calc(var(--below-navbar-view-height) - 68px);
                }
                .after-cutoff-content-height {
                    min-height: var(--below-navbar-view-height);
                }
                path {
                    fill: #fff;
                }
                .section-last {
                    padding: 10px 15px;
                }
                .amount {
                    width: 100px;
                }
                .area-shadow {
                    box-shadow: 0px 3px 12px 0px rgba(0, 0, 0, 0.15);
                }
                .check-image {
                    margin: 5px 10px 5px 0;
                    padding: 5px;
                    border: 2px solid var(--secondary-text-color);
                }
                .badge {
                    color: var(--link-button-text);
                    padding: 3px 5px;
                    border-radius: 5px;
                    background: var(--Gray-Light-30, rgba(230, 230, 230, 0.3));
                }
                .flex {
                    display: flex;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CheckExceptionDetailContainer;
    }
}
